import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { topicApi } from '../services/api.ts';
import { authService } from '../services/auth.ts';
import NavigationMenu from './NavigationMenu.tsx';
import TruncatedDescription from './TruncatedDescription.tsx';
import PresentersList from './PresentersList.tsx';

interface DeletedTopic {
  id: string;
  title: string;
  description: string;
  submittedBy: string;
  presenters?: string[]; // Array of presenter aliases
  teamId: string;
  sessionType: string;
  submittedAt: string;
  deletedAt: string;
  deletedBy: string;
}

interface DeletedTopicsPageProps {
  currentTeam: { teamId: string; teamName: string } | null;
  teams: Array<{ teamId: string; teamName: string }>;
  onBack: () => void;
  onNavigateToPastTopics: () => void;
  onNavigateToMyTopicStats: () => void;
  onNavigateToAbout: () => void;
  onSubmitNewTopic: () => void;
}

const DeletedTopicsPage: React.FC<DeletedTopicsPageProps> = ({ currentTeam, teams, onBack, onNavigateToPastTopics, onNavigateToMyTopicStats, onNavigateToAbout, onSubmitNewTopic }) => {
  const navigate = useNavigate();
  console.log('DeletedTopicsPage: currentTeam:', currentTeam);
  console.log('DeletedTopicsPage: teams:', teams);
  
  const [deletedTopics, setDeletedTopics] = useState<DeletedTopic[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<DeletedTopic[]>([]);
  const [localTeams, setLocalTeams] = useState(teams);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(currentTeam?.teamId || 'all');
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState<string | null>(null);
  const [showPermanentDeleteConfirm, setShowPermanentDeleteConfirm] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isPermanentlyDeleting, setIsPermanentlyDeleting] = useState(false);

  useEffect(() => {
    // Load teams if not provided
    if (teams.length === 0) {
      loadTeams();
    } else {
      setLocalTeams(teams);
    }
  }, [teams]);

  useEffect(() => {
    console.log('DeletedTopicsPage: useEffect triggered, selectedTeam:', selectedTeam);
    fetchDeletedTopics();
  }, [selectedTeam]);

  useEffect(() => {
    console.log('DeletedTopicsPage: filtering topics, searchTerm:', searchTerm);
    filterTopics();
  }, [deletedTopics, searchTerm]);

  const loadTeams = async () => {
    try {
      setLoadingTeams(true);
      const teamsData = await topicApi.getTeams();
      setLocalTeams(teamsData);
    } catch (error) {
      console.error('Error loading teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchDeletedTopics = async () => {
    try {
      console.log('DeletedTopicsPage: fetchDeletedTopics called with selectedTeam:', selectedTeam);
      setLoading(true);
      const topics = await topicApi.getDeletedTopics(selectedTeam);
      console.log('DeletedTopicsPage: received topics:', topics);
      setDeletedTopics(topics);
    } catch (error) {
      console.error('Error fetching deleted topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTopics = () => {
    let filtered = deletedTopics;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(topic => {
        // Search in title and description
        const matchesBasic = topic.title.toLowerCase().includes(term) ||
          topic.description.toLowerCase().includes(term);
        
        // Search across all presenters (including hidden ones)
        const presenters = topic.presenters || [topic.submittedBy];
        const matchesPresenter = presenters.some(p => p.toLowerCase().includes(term));
        
        return matchesBasic || matchesPresenter;
      });
    }

    setFilteredTopics(filtered);
  };

  const handleRestore = async (topicId: string) => {
    try {
      setIsRestoring(true);
      await topicApi.restoreTopic(topicId);
      await fetchDeletedTopics();
      setShowRestoreConfirm(null);
    } catch (error) {
      console.error('Error restoring topic:', error);
      alert('Failed to restore topic. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  const handlePermanentDelete = async (topicId: string) => {
    try {
      setIsPermanentlyDeleting(true);
      await topicApi.permanentlyDeleteTopic(topicId);
      await fetchDeletedTopics();
      setShowPermanentDeleteConfirm(null);
    } catch (error) {
      console.error('Error permanently deleting topic:', error);
      alert('Failed to permanently delete topic. Please try again.');
    } finally {
      setIsPermanentlyDeleting(false);
    }
  };

  const formatDeletedBy = (deletedBy: string) => {
    if (!deletedBy) return 'Unknown';
    // Remove "AmazonFederate_" prefix if present
    return deletedBy.replace(/^AmazonFederate_/, '');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTeamName = (teamId: string) => {
    if (teamId === 'all') return 'All Series';
    const team = localTeams.find(t => t.teamId === teamId);
    return team?.teamName || 'Unknown Team';
  };

  const handleTeamChange = (newTeamId: string) => {
    setSelectedTeam(newTeamId);
    setShowTeamDropdown(false);
    
    // Update URL to reflect team change
    if (newTeamId !== 'all') {
      navigate(`/series/${newTeamId}/deleted-topics`);
    }
  };

  const handleTeamDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showTeamDropdown) {
      loadTeams(); // Load teams when opening dropdown
    }
    setShowTeamDropdown(!showTeamDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showTeamDropdown) {
        setShowTeamDropdown(false);
      }
      if (showUserDropdown) {
        setShowUserDropdown(false);
      }
    };

    if (showTeamDropdown || showUserDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTeamDropdown, showUserDropdown]);

  const handleNavigation = (page: 'main' | 'past-topics' | 'my-topic-stats' | 'deleted-topics' | 'submit-topic' | 'about') => {
    switch (page) {
      case 'main':
        onBack();
        break;
      case 'past-topics':
        onNavigateToPastTopics();
        break;
      case 'my-topic-stats':
        onNavigateToMyTopicStats();
        break;
      case 'about':
        onNavigateToAbout();
        break;
      case 'submit-topic':
        onSubmitNewTopic();
        break;
    }
  };

  return (
    <div className="past-topics-page">
      <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>ShareBrief</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
            <h2 
              onClick={handleTeamDropdownToggle}
              style={{ 
                margin: 0, 
                color: '#2563eb', 
                fontWeight: '600',
                fontSize: '1.5rem',
                cursor: 'pointer'
              }}
            >
              {getTeamName(selectedTeam)}
            </h2>
            <button
              onClick={handleTeamDropdownToggle}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              color: '#666',
              padding: '2px'
            }}
          >
            {loadingTeams ? (
              <span className="loading-spinner">⟳</span>
            ) : (
              '▼'
            )}
          </button>
          {showTeamDropdown && (
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '300px',
                marginTop: '4px'
              }}>
              <button
                onClick={() => handleTeamChange('all')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: selectedTeam === 'all' ? '#f5f5f5' : 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                All Series
              </button>
              {localTeams.map((team) => (
                <button
                  key={team.teamId}
                  onClick={() => handleTeamChange(team.teamId)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: selectedTeam === team.teamId ? '#f5f5f5' : 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {team.teamName}
                </button>
              ))}
            </div>
          )}
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {(() => {
            const userInfo = authService.getUserInfo();
            return userInfo ? (
              <div style={{ position: 'relative' }}>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUserDropdown(!showUserDropdown);
                  }}
                  style={{ 
                    fontSize: '16px', 
                    marginBottom: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Welcome <span style={{ color: '#2563eb', fontWeight: '600' }}>{userInfo.name}</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
                </div>
                
                {showUserDropdown && (
                  <div 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: '0',
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 9999,
                      minWidth: '120px'
                    }}
                  >
                    <button
                      onClick={() => {
                        authService.logout();
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : null;
          })()}
        </div>
      </div>
      
      <NavigationMenu 
        currentPage="deleted-topics" 
        currentTeamId={currentTeam?.teamId}
        onNavigate={handleNavigation}
      />

      <div className="search-section">
        <input
          type="text"
          placeholder="Search by title, description, or presenter..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div style={{ 
          textAlign: 'center', 
          margin: '16px 0', 
          fontSize: '14px', 
          color: '#666',
          fontStyle: 'italic'
        }}>
          Topics listed here were likely accidentally deleted. Restore or permanently delete them as intended.
        </div>
      </div>

      <div className="topics-section">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#666' }}>
            Loading deleted topics...
          </div>
        ) : filteredTopics.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#666' }}>
            {searchTerm ? 'No deleted topics match your search.' : 'No deleted topics found.'}
          </div>
        ) : (
          <table className="topics-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Presenter(s)</th>
                <th>Team</th>
                <th>Presented Date</th>
                <th>Deleted By</th>
                <th>Deleted At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTopics.map((topic) => (
                <tr key={topic.id}>
                  <td>{topic.title}</td>
                  <td><TruncatedDescription description={topic.description} topicId={topic.id} /></td>
                  <td>
                    <PresentersList 
                      presenters={topic.presenters || [topic.submittedBy]}
                      currentUser={authService.getUserInfo()?.username}
                      displayMode="rows"
                    />
                  </td>
                  <td>{getTeamName(topic.teamId)}</td>
                  <td>{topic.presentedAt ? formatDate(topic.presentedAt) : 'Not presented'}</td>
                  <td>{formatDeletedBy(topic.deletedBy)}</td>
                  <td>{formatDate(topic.deletedAt)}</td>
                  <td>
                    <button
                      onClick={() => setShowPermanentDeleteConfirm(topic.id)}
                      className="rate-button-small"
                      style={{ 
                        backgroundColor: '#dc3545', 
                        color: 'white',
                        marginRight: '8px'
                      }}
                    >
                      Permanently Delete
                    </button>
                    <button
                      onClick={() => setShowRestoreConfirm(topic.id)}
                      className="rate-button-small"
                      style={{ backgroundColor: '#28a745', color: 'white' }}
                    >
                      Restore
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="modal-overlay" onClick={() => setShowRestoreConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Restore Topic</h2>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to restore this topic? It will be moved back to the main topics list.</p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowRestoreConfirm(null)}
                style={{ 
                  padding: '8px 16px', 
                  backgroundColor: '#6c757d', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  marginRight: '8px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRestore(showRestoreConfirm)}
                disabled={isRestoring}
                style={{ 
                  padding: '8px 16px', 
                  backgroundColor: isRestoring ? '#6c757d' : '#007bff', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: isRestoring ? 'not-allowed' : 'pointer'
                }}
              >
                {isRestoring ? 'Restoring...' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {showPermanentDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowPermanentDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Permanently Delete Topic</h2>
            </div>
            <div className="modal-body">
              <p><strong>Warning:</strong> This action cannot be undone. The topic will be permanently removed from the database and will no longer be recoverable.</p>
              <p>Are you sure you want to permanently delete this topic?</p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowPermanentDeleteConfirm(null)}
                style={{ 
                  padding: '8px 16px', 
                  backgroundColor: '#6c757d', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  marginRight: '8px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handlePermanentDelete(showPermanentDeleteConfirm)}
                disabled={isPermanentlyDeleting}
                style={{ 
                  padding: '8px 16px', 
                  backgroundColor: isPermanentlyDeleting ? '#6c757d' : '#dc3545', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: isPermanentlyDeleting ? 'not-allowed' : 'pointer'
                }}
              >
                {isPermanentlyDeleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeletedTopicsPage;
